import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * **Eine Chat-Antwort als HTML für Word — aus dem Markdown, nicht aus der Darstellung.**
 *
 * Bis 09.10.2026 wurde das `innerHTML` der gerenderten Antwort kopiert: Tailwind-Klassen,
 * Design-Hintergründe und die Hilfs-Elemente der Streaming-Animation kamen mit, und Word zeigte
 * graue Kästen um die Textteile. Hier entsteht das HTML neu aus dem Markdown: nur schlichte Tags
 * (Überschriften, Absätze, Listen, Tabellen, Hervorhebung, Links), keine Klassen, und nur die
 * wenigen Stilangaben, die Word versteht — Schrift, Tabellenrahmen, Zellabstand.
 *
 * Rohes HTML in der Antwort (Schrittblöcke `<div class="progress">`, Ressourcen-Platzhalter) wird
 * verworfen, nicht als Text ausgegeben.
 */

const FONT = 'font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#000';
const CELL = 'border:1px solid #808080;padding:4px 6px;vertical-align:top';

const components = {
    table: ({ children }: any) => (
        <table style={{ borderCollapse: 'collapse', margin: '6pt 0' }} cellPadding={4}>
            {children}
        </table>
    ),
    th: ({ children }: any) => <th style={cssToObject(`${CELL};font-weight:bold;text-align:left`)}>{children}</th>,
    td: ({ children }: any) => <td style={cssToObject(CELL)}>{children}</td>,
    code: ({ children }: any) => <code style={{ fontFamily: 'Consolas,monospace' }}>{children}</code>,
    pre: ({ children }: any) => <pre style={{ fontFamily: 'Consolas,monospace', whiteSpace: 'pre-wrap' }}>{children}</pre>,
    // Ohne die Klassen, die remark-gfm an Aufgabenlisten hängt („contains-task-list").
    ul: ({ children }: any) => <ul>{children}</ul>,
    ol: ({ children, start }: any) => <ol start={start}>{children}</ol>,
    li: ({ children }: any) => <li>{children}</li>,
    // Aufgaben-Kästchen „- [ ]" als Zeichen statt als Formularfeld — Word übernimmt Eingabefelder nicht.
    input: ({ checked }: any) => <span>{checked ? '☑ ' : '☐ '}</span>,
};

function cssToObject(css: string): React.CSSProperties {
    return Object.fromEntries(
        css
            .split(';')
            .map(rule => rule.split(':').map(x => x.trim()))
            .filter(([k, v]) => k && v)
            .map(([k, v]) => [k.replace(/-([a-z])/g, (_, c) => c.toUpperCase()), v])
    ) as React.CSSProperties;
}

/** Schrittblöcke und andere rohe HTML-Blöcke aus dem Markdown entfernen. */
export const stripRawBlocks = (markdown: string): string =>
    markdown
        .replace(/<div\s+class=["']progress["'][^>]*>.*?<\/div>/gis, '')
        .replace(/<div\s+class=["']inline-resource["'][^>]*>\s*<\/div>/gis, '')
        .replace(/```payload[\s\S]*?```/g, '')
        .trim();

export const answerClipboardHtml = (markdown: string): string => {
    const body = renderToStaticMarkup(
        <Markdown remarkPlugins={[remarkGfm]} skipHtml components={components}>
            {stripRawBlocks(markdown)}
        </Markdown>
    );
    return `<div style="${FONT}">${body}</div>`;
};

/** Der reine Text derselben Antwort — für Programme ohne HTML. */
export const answerClipboardText = (markdown: string): string => {
    const holder = document.createElement('div');
    holder.innerHTML = answerClipboardHtml(markdown);
    return (holder.textContent ?? '').trim();
};
